import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getEntityColumns } from "services/entityDynamicFieldService";
import { getJobColumns } from "services/jobsService";
import { useUserPreference, PREFERENCE_TYPES } from "./useUserPreference";

/**
 * Custom hook to load available columns for an entity type and manage column preferences.
 * @param {number} entityTypeCode 
 * @param {string} localStorageKey 
 * @returns {Object} availableColumns, selectedColumnsForApi, visibility, setVisibility, isLoading
 */
export function useDynamicColumns(entityTypeCode, localStorageKey, refreshKey = 0) {
  const [availableColumns, setAvailableColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [internalRefresh, setInternalRefresh] = useState(0);

  const refetchColumns = useCallback(() => {
    setInternalRefresh((prev) => prev + 1);
  }, []);

  // Load columns definition from the database
  // refreshKey or internalRefresh can be triggered to force a re-fetch
  useEffect(() => {
    let active = true;
    setIsLoading(true);

    const fetchPromise = entityTypeCode === 2
      ? getJobColumns()
      : getEntityColumns(entityTypeCode);

    fetchPromise
      .then((res) => {
        if (active && res.success && res.data) {
          setAvailableColumns(res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to load entity columns:", err);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [entityTypeCode, refreshKey, internalRefresh]);

  const effectivePageKey = (localStorageKey || `entity_columns_${entityTypeCode}`).replace(/-/g, "_");

  // Sync column visibility with backend database
  const { preferenceState: apiVisibility, savePreference: saveApiVisibility, isPreferenceLoading } = useUserPreference({
    pageKey: effectivePageKey,
    typeKey: PREFERENCE_TYPES.COLUMN_VISIBILITY,
    defaultState: {},
  });

  const [localVisibility, setLocalVisibility] = useState({});

  const apiVisibilityRef = useRef(apiVisibility);
  useEffect(() => {
    apiVisibilityRef.current = apiVisibility;
  }, [apiVisibility]);

  const saveApiVisibilityRef = useRef(saveApiVisibility);
  useEffect(() => {
    saveApiVisibilityRef.current = saveApiVisibility;
  }, [saveApiVisibility]);

  const visibility = useMemo(() => {
    return { ...(apiVisibility || {}), ...localVisibility };
  }, [apiVisibility, localVisibility]);

  const setVisibility = useCallback((updater) => {
    setLocalVisibility((prevLocal) => {
      const currentFull = { ...(apiVisibilityRef.current || {}), ...prevLocal };
      const nextFull = typeof updater === "function" ? updater(currentFull) : updater;
      saveApiVisibilityRef.current?.(nextFull);
      return nextFull;
    });
  }, []);

  // Initialize visibility settings: default all available columns (including newly added ones) to true (visible)
  useEffect(() => {
    if (availableColumns.length === 0 || isPreferenceLoading) return;
    
    const current = apiVisibilityRef.current || {};
    const updated = { ...current };
    let changed = false;
    availableColumns.forEach((col) => {
      if (col.ColumnKey && updated[col.ColumnKey] === undefined) {
        updated[col.ColumnKey] = true;
        changed = true;
      }
    });

    if (changed) {
      saveApiVisibilityRef.current?.(updated);
    }
  }, [availableColumns, isPreferenceLoading]);

  // Derive the column keys to pass to the backend API (only the visible/selected ones)
  const derivedColumns = useMemo(() => {
    return availableColumns
      .filter((col) => visibility[col.ColumnKey] !== false)
      .map((col) => col.ColumnKey);
  }, [availableColumns, visibility]);

  const prevColumnsRef = useRef([]);

  const selectedColumnsForApi = useMemo(() => {
    const isSame =
      derivedColumns.length === prevColumnsRef.current.length &&
      derivedColumns.every((val, index) => val === prevColumnsRef.current[index]);

    if (!isSame) {
      prevColumnsRef.current = derivedColumns;
    }
    return prevColumnsRef.current;
  }, [derivedColumns]);

  return {
    availableColumns,
    selectedColumnsForApi,
    visibility,
    setVisibility,
    isLoading,
    refetchColumns,
  };
}
